// Player management service
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  runTransaction,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { v4 as uuidv4 } from 'uuid';
import { normalizeDisplayName } from '../utils/displayName';
import { generateSalt, hashPin } from '../utils/crypto';

// Create a new player (with display name uniqueness check)
export const createPlayer = async (competitionId, displayName, pin, providedPlayerId = null, odplayerId = null) => {
  const playerId = providedPlayerId || uuidv4();
  const displayNameKey = normalizeDisplayName(displayName);

  // Use transaction to ensure display name uniqueness
  const result = await runTransaction(db, async (transaction) => {
    const nameIndexRef = doc(db, 'competitions', competitionId, 'nameIndex', displayNameKey);
    const nameIndexSnap = await transaction.get(nameIndexRef);

    if (nameIndexSnap.exists()) {
      throw new Error('Display name already taken in this competition');
    }

    // Hash the PIN
    const salt = generateSalt();
    const pinHash = await hashPin(pin, salt);

    // Create player
    const playerRef = doc(db, 'players', playerId);
    const player = {
      competitionId,
      displayName: displayName.trim(),
      displayNameKey,
      pinHash,
      pinSalt: salt,
      status: 'active',
      joinedAt: serverTimestamp(),
      ...(odplayerId && { odplayerId }) // Link to auth account if provided
    };

    transaction.set(playerRef, player);

    // Create name index entry
    transaction.set(nameIndexRef, { playerId });

    return {
      id: playerId,
      ...player
    };
  });

  return result;
};

// Get player by ID
export const getPlayer = async (playerId) => {
  const playerRef = doc(db, 'players', playerId);
  const playerSnap = await getDoc(playerRef);

  if (!playerSnap.exists()) {
    return null;
  }

  return {
    id: playerSnap.id,
    ...playerSnap.data()
  };
};

// Get all players for a competition
export const getPlayersForCompetition = async (competitionId) => {
  const playersRef = collection(db, 'players');
  const q = query(playersRef, where('competitionId', '==', competitionId));
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
};

// Get active players for a competition
export const getActivePlayers = async (competitionId) => {
  const playersRef = collection(db, 'players');
  const q = query(
    playersRef,
    where('competitionId', '==', competitionId),
    where('status', '==', 'active')
  );
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
};

// Find player by display name in a competition
export const findPlayerByDisplayName = async (competitionId, displayName) => {
  const displayNameKey = normalizeDisplayName(displayName);
  const nameIndexRef = doc(db, 'competitions', competitionId, 'nameIndex', displayNameKey);
  const nameIndexSnap = await getDoc(nameIndexRef);

  if (!nameIndexSnap.exists()) {
    return null;
  }

  const { playerId } = nameIndexSnap.data();
  return await getPlayer(playerId);
};

// Update player status
export const updatePlayerStatus = async (playerId, status) => {
  const playerRef = doc(db, 'players', playerId);
  await updateDoc(playerRef, {
    status,
    updatedAt: serverTimestamp()
  });
};

// Link player to auth account (set odplayerId)
export const linkPlayerToAuthAccount = async (playerId, authUid) => {
  const playerRef = doc(db, 'players', playerId);
  await updateDoc(playerRef, {
    odplayerId: authUid,
    updatedAt: serverTimestamp()
  });
};

// Reset player PIN (organizer function)
export const resetPlayerPin = async (playerId, newPin) => {
  const salt = generateSalt();
  const pinHash = await hashPin(newPin, salt);

  const playerRef = doc(db, 'players', playerId);
  await updateDoc(playerRef, {
    pinHash,
    pinSalt: salt,
    updatedAt: serverTimestamp()
  });
};

// Verify player PIN
export const verifyPlayerPin = async (playerId, pin) => {
  const player = await getPlayer(playerId);

  if (!player) {
    return false;
  }

  const { pinHash: storedHash, pinSalt: salt } = player;
  const computedHash = await hashPin(pin, salt);

  return computedHash === storedHash;
};

// Count active players in competition
export const countActivePlayers = async (competitionId) => {
  const players = await getActivePlayers(competitionId);
  return players.length;
};

// Find player by odplayerId in a competition
export const findPlayerByOdplayerId = async (competitionId, odplayerId) => {
  if (!odplayerId) return null;

  const playersRef = collection(db, 'players');
  const q = query(
    playersRef,
    where('competitionId', '==', competitionId),
    where('odplayerId', '==', odplayerId)
  );
  const querySnapshot = await getDocs(q);

  if (querySnapshot.empty) {
    return null;
  }

  const doc = querySnapshot.docs[0];
  return {
    id: doc.id,
    ...doc.data()
  };
};

// Create a player from league member (no PIN required - they use their account)
export const createPlayerFromLeagueMember = async (competitionId, displayName, odplayerId) => {
  // First check if this user already exists in the competition (by odplayerId)
  if (odplayerId) {
    const existingPlayer = await findPlayerByOdplayerId(competitionId, odplayerId);
    if (existingPlayer) {
      // Player already exists in this competition, return existing player
      return existingPlayer;
    }
  }

  const playerId = uuidv4();
  const displayNameKey = normalizeDisplayName(displayName);

  // Use transaction to ensure display name uniqueness
  const result = await runTransaction(db, async (transaction) => {
    const nameIndexRef = doc(db, 'competitions', competitionId, 'nameIndex', displayNameKey);
    const nameIndexSnap = await transaction.get(nameIndexRef);

    if (nameIndexSnap.exists()) {
      // Name already taken, skip this player
      return null;
    }

    // Create player without PIN (they authenticate via their account)
    const playerRef = doc(db, 'players', playerId);
    const player = {
      competitionId,
      displayName: displayName.trim(),
      displayNameKey,
      status: 'active',
      joinedAt: serverTimestamp(),
      // Only include odplayerId if it's defined (Firestore doesn't allow undefined values)
      ...(odplayerId && { odplayerId })
    };

    transaction.set(playerRef, player);

    // Create name index entry
    transaction.set(nameIndexRef, { playerId });

    return {
      id: playerId,
      ...player
    };
  });

  return result;
};
